import * as React from "react";
import { Box, Stack, Alert, CircularProgress, Button } from "@mui/material";
import { MessageSquarePlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getProject, updateProject, listSessionsForProject, type Project, type ProjectSession } from "../../api/projects";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { DocumentRow } from "../../api/documentsApi";
import { PageHeader } from "../Common/PageHeader";
import { ProjectInstructionsEditor } from "./ProjectInstructionsEditor";
import { ProjectConversationsList } from "./ProjectConversationsList";
import { ProjectKnowledgeList } from "./ProjectKnowledgeList";
import { ProjectActionsMenu } from "./ProjectActionsMenu";

interface Props { projectId: string }

export function ProjectPage({ projectId }: Props) {
  const navigate = useNavigate();
  const { documents } = useAuthedApi();
  const [project, setProject] = React.useState<Project | null>(null);
  const [sessions, setSessions] = React.useState<ProjectSession[]>([]);
  const [files, setFiles] = React.useState<DocumentRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(() => {
    getProject(projectId)
      .then(setProject)
      .catch((e) => setError(String(e)));
    // Conversations + files are best-effort: a failure here shouldn't blank
    // the whole page (the project header + instructions still load).
    listSessionsForProject(projectId)
      .then(setSessions)
      .catch(() => setSessions([]));
  }, [projectId]);

  const refreshFiles = React.useCallback(() => {
    if (!documents) return;
    documents
      .listByProject(projectId)
      .then(setFiles)
      .catch(() => setFiles([]));
  }, [documents, projectId]);

  React.useEffect(() => { refresh(); }, [refresh]);
  React.useEffect(() => { refreshFiles(); }, [refreshFiles]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!project) return <CircularProgress />;

  // Start a new chat already scoped to this project. The chat screen reads
  // ?project_id from the URL and pre-selects the project for the first turn.
  const startChat = () => navigate(`/?project_id=${encodeURIComponent(project.id)}`);

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: "auto" }}>
      <PageHeader
        title={project.name}
        back
        backTo="/workspace/projects"
        actions={
          <>
            <Button variant="contained" data-ph="header-create_chat" startIcon={<MessageSquarePlus size={16} />} onClick={startChat}>
              New chat
            </Button>
            <ProjectActionsMenu
              projectId={project.id}
              isArchived={project.archived_at !== null}
              currentName={project.name}
              onChanged={refresh}
            />
          </>
        }
      />
      <Stack spacing={2}>
        <ProjectInstructionsEditor
          projectId={project.id}
          initialInstructions={project.instructions}
          onSave={async (text) => {
            await updateProject(project.id, { instructions: text });
            refresh();
          }}
        />
        <ProjectKnowledgeList
          projectId={project.id}
          documents={files}
          onChanged={refreshFiles}
        />
        <ProjectConversationsList projectId={project.id} sessions={sessions} />
      </Stack>
    </Box>
  );
}
